import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Briefcase, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, errorMessage } from "@/lib/api";
import { PortalLinkSheet } from "@/components/PortalLinkSheet";
import { PER_PAGE, Pager } from "@/components/Pager";
import { useAuth } from "@/context/AuthContext";

const BLANK = { name: "", address: "", city: "", email: "", phone: "", tax_id: "", notes: "" };

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const { user } = useAuth();
  const isOwner = user?.role !== "clerk";
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/clients");
      setClients(data);
    } catch (error) {
      toast.error(errorMessage(error, "Could not load clients"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(BLANK);
    setOpen(true);
  };

  const openEdit = (client) => {
    setEditing(client);
    setForm({ ...BLANK, ...client });
    setOpen(true);
  };

  const save = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      toast.error("Client name is required");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name,
      address: form.address,
      city: form.city,
      email: form.email,
      phone: form.phone,
      tax_id: form.tax_id,
      notes: form.notes,
    };
    try {
      if (editing) await api.put(`/clients/${editing.client_id}`, payload);
      else await api.post("/clients", payload);
      toast.success(editing ? "Client updated" : "Client added");
      setOpen(false);
      load();
    } catch (error) {
      toast.error(errorMessage(error, "Could not save client"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (client) => {
    if (!window.confirm(`Delete ${client.name}?`)) return;
    try {
      await api.delete(`/clients/${client.client_id}`);
      toast.success("Client deleted");
      load();
    } catch (error) {
      toast.error(errorMessage(error, "Could not delete client"));
    }
  };

  const field = (key) => ({
    value: form[key] ?? "",
    onChange: (e) => setForm((f) => ({ ...f, [key]: e.target.value })),
  });

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return clients;
    return clients.filter((c) =>
      [c.name, c.city, c.email, c.tax_id]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle)),
    );
  }, [clients, query]);

  useEffect(() => {
    setPage(1);
  }, [query]);

  useEffect(() => {
    setQuery(searchParams.get("q") || "");
  }, [searchParams]);

  return (
    <div className="space-y-8" data-testid="clients-page">
      <header className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="label-caps">Litigants you act for</p>
          <h1 className="mt-3 font-serif text-3xl tracking-tight text-slate-900 lg:text-4xl">
            Clients
          </h1>
          <p className="mt-3 max-w-xl text-sm text-slate-600">
            The party whose cause you argue. Each client carries its own address, GST number and
            contact persons — and its ledger stands in the client&rsquo;s own name when you bill it
            directly.
          </p>
        </div>
        <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search clients…"
          className="w-full rounded-sm sm:w-64"
          data-testid="clients-search"
        />
        <button
          type="button"
          onClick={openNew}
          data-testid="add-client-button"
          className="inline-flex items-center gap-2 bg-slate-900 px-5 py-2.5 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800"
        >
          <Plus className="h-4 w-4" strokeWidth={2} />
          Add client
        </button>
        </div>
      </header>

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      ) : clients.length === 0 ? (
        <div className="surface p-10 text-center" data-testid="clients-empty">
          <Briefcase className="mx-auto h-6 w-6 text-slate-300" strokeWidth={1.4} />
          <p className="mt-4 text-sm text-slate-600">
            No clients yet. Add the litigants you appear for.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
        <div className="grid gap-4 lg:grid-cols-2" data-testid="clients-list">
          {filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE).map((client) => (
            <article
              key={client.client_id}
              className="surface flex flex-col p-6"
              data-testid={`client-card-${client.client_id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <Link
                    to={`/clients/${client.client_id}`}
                    data-testid={`open-client-ledger-${client.client_id}`}
                    className="font-serif text-lg leading-snug text-slate-900 underline-offset-4 transition-colors duration-150 hover:text-brand-terracotta hover:underline"
                  >
                    {client.name}
                  </Link>
                  <p className="mt-1 text-[12px] text-slate-500">
                    {client.city || "City not set"}
                  </p>
                </div>
                <div className="flex gap-1">
                  <PortalLinkSheet scope="client" id={client.client_id} name={client.name} />
                  <button
                    type="button"
                    onClick={() => openEdit(client)}
                    data-testid={`edit-client-${client.client_id}`}
                    className="flex h-8 w-8 items-center justify-center border border-slate-200 text-slate-500 transition-colors duration-150 hover:border-slate-400 hover:text-slate-900"
                  >
                    <Pencil className="h-3.5 w-3.5" strokeWidth={1.6} />
                  </button>
                  {isOwner && (
                  <button
                    type="button"
                    onClick={() => remove(client)}
                    data-testid={`delete-client-${client.client_id}`}
                    className="flex h-8 w-8 items-center justify-center border border-slate-200 text-slate-500 transition-colors duration-150 hover:border-red-300 hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.6} />
                  </button>
                  )}
                </div>
              </div>

              <dl className="mt-5 grid grid-cols-2 gap-4 border-t border-slate-100 pt-4 text-[12px]">
                <div className="col-span-2">
                  <dt className="label-caps">Address</dt>
                  <dd className="mt-1 whitespace-pre-line text-slate-800">
                    {client.address || "—"}
                  </dd>
                </div>
                <div>
                  <dt className="label-caps">Email</dt>
                  <dd className="mt-1 break-all text-slate-800">{client.email || "—"}</dd>
                </div>
                <div>
                  <dt className="label-caps">Phone</dt>
                  <dd className="num mt-1 text-slate-800">{client.phone || "—"}</dd>
                </div>
                <div>
                  <dt className="label-caps">GST / Tax no.</dt>
                  <dd className="num mt-1 text-slate-800">{client.tax_id || "—"}</dd>
                </div>
                <div>
                  <dt className="label-caps">On record</dt>
                  <dd className="mt-1 text-slate-800">
                    {client.contact_count} contact person(s) · {client.matter_count} matter(s)
                  </dd>
                </div>
              </dl>
            </article>
          ))}
        </div>
        {filtered.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="clients-no-match">
            No client matches &ldquo;{query.trim()}&rdquo;.
          </p>
        )}
        <Pager page={page} total={filtered.length} onPage={setPage} testId="clients-pager" />
        </div>
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle className="font-serif text-xl">
              {editing ? "Edit client" : "Add client"}
            </SheetTitle>
            <SheetDescription className="text-[12px]">
              These details print in the CLIENT column of a fee note when you choose to show both
              the billing party and the client.
            </SheetDescription>
          </SheetHeader>
          <form className="mt-7 space-y-5" onSubmit={save} data-testid="client-form">
            <div className="space-y-2">
              <Label className="label-caps">Client name *</Label>
              <Input
                {...field("name")}
                placeholder="Kaveri Infra Ltd."
                data-testid="client-name-input"
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Address</Label>
              <Textarea
                {...field("address")}
                rows={3}
                data-testid="client-address-input"
                className="rounded-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="label-caps">City</Label>
                <Input {...field("city")} data-testid="client-city-input" className="rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">GST / Tax no.</Label>
                <Input
                  {...field("tax_id")}
                  data-testid="client-tax-input"
                  className="num rounded-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="label-caps">Email</Label>
                <Input
                  {...field("email")}
                  type="email"
                  data-testid="client-email-input"
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Phone</Label>
                <Input
                  {...field("phone")}
                  data-testid="client-phone-input"
                  className="num rounded-sm"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Notes</Label>
              <Textarea
                {...field("notes")}
                rows={2}
                data-testid="client-notes-input"
                className="rounded-sm"
              />
            </div>
            <button
              type="submit"
              disabled={saving}
              data-testid="save-client-button"
              className="inline-flex w-full items-center justify-center gap-2 bg-slate-900 px-5 py-3 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800 disabled:opacity-60"
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {editing ? "Save changes" : "Add client"}
            </button>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}
